import { push } from 'connected-react-router';
import { withFormik } from 'formik';
import truncate from 'lodash/truncate';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { ANALYTICS_EVENTS, UNIFIED } from 'src/app/analytics/analyticsEvents';
import { errorBoundary, withStandardPageAndLoading, withStripeElements } from 'src/app/hocs';
import * as paths from 'src/app/router/paths';
import { segmentAnalyticsEvent, segmentTrack } from 'src/app/state/actions';
import { postPaymentCard } from 'src/app/state/actions/account';
import { updateCartPaymentMethod } from 'src/app/state/actions/cart';
import {
  getBFFCart,
  getCurrentAccountId,
  getCurrentCartId,
  getCurrentCartUUID,
  getCurrentOrderId,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import Renderer from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const cartUUID = getCurrentCartUUID(state);
    const orderId = getCurrentOrderId(state);
    const accountId = getCurrentAccountId(state);
    const bffCart = getBFFCart(state);
    const cartId = getCurrentCartId(state);

    return {
      cartUUID,
      orderId,
      accountId,
      total: bffCart.total,
      subtotal: bffCart.subtotal,
      preCreditTotal: bffCart.preCreditTotal,
      cartId,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchSaveCard: async (stripe, cartUUID) => {
    const cardId = await dispatch(postPaymentCard(stripe));
    await dispatch(updateCartPaymentMethod({ cartUUID, paymentMethodId: cardId }));
  },
  dispatchAddCreditCardFailure: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.addCreditCardFailure, { ...attributes })),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchSelectCard: cartId => dispatch(push(paths.checkoutSelectCreditCard.toPath({ cartId }))),
});

const enhanceWithFormik = withFormik({
  displayName: 'CreditCardForm',
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const {
      stripe,
      dispatchSaveCard,
      dispatchAddCreditCardFailure,
      cartUUID,
      dispatchSegmentTrack,
      cartId,
      dispatchSelectCard,
    } = props;

    const dispatchAnalytics = (errorCode: any, errorSummary: any) => {
      dispatchAddCreditCardFailure({
        error_code: errorCode,
        error_summary: errorSummary,
      });
    };

    setSubmitting(true);

    try {
      await dispatchSaveCard(stripe, cartUUID);
      dispatchSegmentTrack(UNIFIED.addACreditCard, { source: 'checkout' });
      dispatchSelectCard(cartId);
    } catch (error) {
      const bffError = error?.error;
      const errorCode = bffError?.errorCode;
      const errorSummary = bffError?.flavorText;

      dispatchAnalytics(errorCode ? errorCode : '400', errorSummary ? errorSummary : '');

      setErrors({
        _generic: errorSummary ? truncate(errorSummary, { length: 115, omission: '...' }) : undefined,
        cardNumber: containsAnyKeyword(errorSummary, ['card number']) ? ' ' : undefined,
        expirationDate: containsAnyKeyword(errorSummary, ['expiration']) ? ' ' : undefined,
        securityCode: containsAnyKeyword(errorSummary, ['CVC', 'security code']) ? ' ' : undefined,
      });

      setSubmitting(false);
    }
  },
});

function containsAnyKeyword(input: string | undefined, keywords: string[]): boolean {
  const lowercasedInput = input?.toLowerCase();
  return keywords.some(keyword => lowercasedInput?.includes(keyword.toLowerCase()) ?? false);
}

export default compose(
  setDisplayName('paymentCardCreate'),
  errorBoundary,
  withStripeElements,
  connect(makeMapStateToProps, mapDispatchToProps),
  enhanceWithFormik,
  withStandardPageAndLoading('Add a credit card')
)(Renderer);
